/**
 * Where a spoken reply goes when an agent is already waiting on a question.
 *
 * Split out of `voice-dispatch` because the answer to "is anyone waiting?" is
 * not always in this window. A cursor question arrives over the live cursor
 * channel and is filtered by space; a window that missed it, or that thinks it
 * is in a different space, sees nothing pending. The old code treated that as
 * "nobody is waiting" and sent the words as a fresh prompt instead.
 *
 * Measured on a real session: the agent asked which display was meant, the user
 * answered out loud, and the reply became a new request that died in the queue
 * sixty seconds later because the first one still held the session. The
 * question stayed unanswered, and nothing said so.
 *
 * So local state is the fast path, not the only one: when it has nothing, the
 * server is asked before falling through.
 */

import {
  listPendingAwaitingInputs,
  replyAwaitingInput,
} from "@/api/awaiting-input";
import { useCanvasStore } from "@/stores/canvas-store";
import { useFramesStore } from "@/stores/frames-store";
import { useProjectStore } from "@/stores/project-store";
import { submitQuestionAttachments } from "@/lib/submit-question-attachments";
import type { ScreenRecordingsContext } from "@/lib/screen-recordings-context";

export type AwaitingVoiceReplyTarget =
  | { kind: "cursor"; awaitingInputId: string; agentId: string }
  | { kind: "frame"; awaitingInputId: string };

/** An open question this window shows, with the run that asked it. */
type LocalAwaitingQuestion = {
  target: AwaitingVoiceReplyTarget;
  executionId: string | null;
};

/** The one open question a spoken reply answers; none when two are open. */
function getLocalAwaitingQuestion(): LocalAwaitingQuestion | null {
  const canvas = useCanvasStore.getState();
  const currentSpaceId = useProjectStore.getState().currentSpace?.id;
  const questionsByAwaitingId = new Map<string, LocalAwaitingQuestion>();

  for (const [agentId, cursor] of canvas.agentCursors) {
    if (!cursor.question) continue;
    if (currentSpaceId && cursor.spaceId !== currentSpaceId) continue;
    questionsByAwaitingId.set(cursor.question.awaitingInputId, {
      target: {
        kind: "cursor",
        awaitingInputId: cursor.question.awaitingInputId,
        agentId,
      },
      executionId: cursor.executionId ?? null,
    });
  }

  const { framesById } = useFramesStore.getState();
  for (const [frameId, list] of canvas.awaitingInputs) {
    if (!framesById[frameId]) continue;
    for (const awaiting of list) {
      if (!questionsByAwaitingId.has(awaiting.id)) {
        questionsByAwaitingId.set(awaiting.id, {
          target: { kind: "frame", awaitingInputId: awaiting.id },
          executionId: awaiting.executionId,
        });
      }
    }
  }
  return questionsByAwaitingId.size === 1
    ? (questionsByAwaitingId.values().next().value ?? null)
    : null;
}

function getAwaitingVoiceReplyTarget(): AwaitingVoiceReplyTarget | null {
  return getLocalAwaitingQuestion()?.target ?? null;
}

/** The one open question this window shows, and the run that asked it. */
export function awaitingVoiceReplyQuestion(): {
  awaitingInputId: string;
  executionId: string | null;
} | null {
  const question = getLocalAwaitingQuestion();
  return question
    ? {
        awaitingInputId: question.target.awaitingInputId,
        executionId: question.executionId,
      }
    : null;
}

/** Calls `onChange` whenever the canvas's open questions may have changed. */
export function watchAwaitingVoiceReplyTargets(
  onChange: () => void,
): () => void {
  return useCanvasStore.subscribe((state, previous) => {
    if (
      state.agentCursors !== previous.agentCursors ||
      state.awaitingInputs !== previous.awaitingInputs
    )
      onChange();
  });
}

export async function answerAwaitingInputFromVoice(
  target: AwaitingVoiceReplyTarget,
  answer: string,
  imageIds: string[] = [],
  isCancelled: () => boolean = () => false,
  screenRecordings?: ScreenRecordingsContext,
): Promise<boolean> {
  if (isCancelled()) return false;
  const selectedOption = "other";
  if (target.awaitingInputId.startsWith("onboarding:")) {
    window.dispatchEvent(
      new CustomEvent("denker:onboarding-cursor-question-answer", {
        detail: {
          awaitingInputId: target.awaitingInputId,
          answer,
          selectedOption,
        },
      }),
    );
    if (target.kind === "cursor") {
      useCanvasStore.getState().clearAgentCursorQuestion(target.agentId);
    }
    return true;
  }

  const projectId = useProjectStore.getState().currentProject?.id;
  if (!projectId) return false;

  if (imageIds.length || screenRecordings) {
    const local = [...useCanvasStore.getState().awaitingInputs.values()]
      .flat()
      .find((item) => item.id === target.awaitingInputId);
    const remote = local?.conversationId
      ? undefined
      : await withDeadline(listPendingAwaitingInputs(projectId));
    const pending =
      remote === TIMED_OUT
        ? undefined
        : remote?.find((item) => item.id === target.awaitingInputId);
    if (
      isCancelled() ||
      projectId !== useProjectStore.getState().currentProject?.id
    )
      return false;
    const sent = await submitQuestionAttachments({
      projectId,
      conversationId: local?.conversationId ?? pending?.conversation_id,
      executionId: local?.executionId ?? pending?.execution,
      awaitingInputId: target.awaitingInputId,
      text: answer,
      imageIds,
      isCancelled,
      screenRecordings,
    });
    if (!sent) return false;
  } else {
    if (isCancelled()) return false;
    await replyAwaitingInput(projectId, target.awaitingInputId, {
      answer,
      selectedOption,
    });
  }

  if (target.kind === "cursor") {
    useCanvasStore.getState().clearAgentCursorQuestion(target.agentId);
  } else {
    useCanvasStore.getState().removeAwaitingInput(target.awaitingInputId);
  }
  return true;
}

/**
 * The target a spoken reply should answer, asking the server when this window
 * cannot see one.
 *
 * Local state first, because it is free and almost always right. The fetch only
 * happens when nothing is pending locally - the case that used to send the
 * user's words off as a new request - and it is bounded by the same silent
 * failure the API client already uses: if it cannot answer, the caller falls
 * through exactly as before.
 *
 * Only a single pending question is answered. Two open questions and a spoken
 * sentence is a guess about which one it belongs to, and guessing wrong here
 * answers the wrong agent.
 */
export async function resolveAwaitingVoiceReplyTarget(): Promise<AwaitingVoiceReplyTarget | null> {
  const local = getAwaitingVoiceReplyTarget();
  if (local) return local;

  // Only agents the user can actually see, in the space they are speaking into.
  // Nobody can be waiting on an answer if no agent is here, and the question
  // this is reaching for belongs to one of these - a window that holds *part*
  // of an agent's state, not one that holds none.
  const { agentCursors } = useCanvasStore.getState();
  const currentSpaceId = useProjectStore.getState().currentSpace?.id;
  const visibleAgentIds = new Set(
    [...agentCursors]
      .filter(
        ([, cursor]) => !currentSpaceId || cursor.spaceId === currentSpaceId,
      )
      .map(([agentId]) => agentId),
  );
  if (visibleAgentIds.size === 0) return null;

  const projectId = useProjectStore.getState().currentProject?.id;
  if (!projectId) return null;

  let pending:
    | Awaited<ReturnType<typeof listPendingAwaitingInputs>>
    | typeof TIMED_OUT;
  try {
    pending = await withDeadline(listPendingAwaitingInputs(projectId));
  } catch {
    return null;
  }
  if (pending === TIMED_OUT) return null;

  // A frameless question is anchored to the agent's cursor; `agent_id` is the
  // cursor key the canvas store records it under, so answering clears the same
  // overlay the user is looking at. A question belonging to an agent this
  // window cannot see is left alone: the user is not looking at it, and
  // consuming their sentence as its answer would be worse than sending it on.
  const answerable = pending.filter(
    (awaiting) =>
      awaiting.frame === null &&
      awaiting.agent_id !== null &&
      visibleAgentIds.has(awaiting.agent_id),
  );
  // One question, or none. Two open at once and a spoken sentence is a guess
  // about which it belongs to, and guessing wrong answers the wrong agent.
  const [awaiting] = answerable;
  if (answerable.length !== 1 || !awaiting?.agent_id) return null;
  return {
    kind: "cursor",
    awaitingInputId: awaiting.id,
    agentId: awaiting.agent_id,
  };
}

/** Nothing came back in time. */
const TIMED_OUT = Symbol("awaiting-lookup-timeout");

/**
 * How long the user waits to find out whether an agent was asking something.
 *
 * `apiRequest` has no timeout, so without this a server that accepts the
 * connection and never answers would hang the prompt before the user has seen
 * so much as an optimistic message - no toast, no draft restore, no trace. The
 * same failure this file exists to stop, one layer down.
 *
 * Short because it sits in front of speech. If the answer has not arrived by
 * then, sending the words on as a prompt is the better of the two mistakes.
 */
const AWAITING_LOOKUP_TIMEOUT_MS = 700;

async function withDeadline<T>(
  work: Promise<T>,
): Promise<T | typeof TIMED_OUT> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<typeof TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), AWAITING_LOOKUP_TIMEOUT_MS);
  });
  try {
    return await Promise.race([work, deadline]);
  } finally {
    clearTimeout(timer);
  }
}
